import { ModificationHandler, Modification, ModificationAction } from '@core/types';

export class ImageModificationHandler implements ModificationHandler {
  apply(element: Element, modification: Modification): void {
    if (modification.action === ModificationAction.REPLACE_IMAGE && modification.value) {
      this.replaceImage(element, modification.value);
    }
  }

  validate(modification: Modification): boolean {
    if (modification.action !== ModificationAction.REPLACE_IMAGE) {
      return false;
    }

    // For replace_image, we need either value (image_url) or media_temp_token
    return !!(modification.value || modification.media_temp_token);
  }

  private replaceImage(element: Element, newImageUrl: string): void {
    // Handle img elements
    if (element instanceof HTMLImageElement) {
      // Store original src if not already stored
      if (!(element as any).dataset.originalSrc) {
        (element as any).dataset.originalSrc = element.src;
      }

      // Store original alt if not already stored
      if (element.alt && !(element as any).dataset.originalAlt) {
        (element as any).dataset.originalAlt = element.alt;
      }

      element.src = newImageUrl;
    } else {
      // Handle background images for other elements
      const computedStyle = window.getComputedStyle(element);
      const currentBg = computedStyle.backgroundImage;

      // Store original background if not already stored
      if (currentBg && currentBg !== 'none' && !(element as any).dataset.originalBackground) {
        (element as any).dataset.originalBackground = currentBg;
      }

      // Set new background image
      (element as HTMLElement).style.backgroundImage = `url(${newImageUrl})`;

      // Ensure background image is displayed properly
      (element as HTMLElement).style.backgroundRepeat = 'no-repeat';
      (element as HTMLElement).style.backgroundPosition = 'center';
      (element as HTMLElement).style.backgroundSize = 'cover';
    }

    // Add tracking attributes
    (element as any).dataset.campaignImageModified = 'true';
    (element as any).dataset.campaignImageUrl = newImageUrl;
  }

  remove(element: Element): void {
    // Restore original image if we have it stored
    if (element instanceof HTMLImageElement && (element as any).dataset.originalSrc) {
      element.src = (element as any).dataset.originalSrc;

      // Restore original alt if we have it stored
      if ((element as any).dataset.originalAlt) {
        element.alt = (element as any).dataset.originalAlt;
      }
    } else {
      // Restore original background
      if ((element as any).dataset.originalBackground) {
        (element as HTMLElement).style.backgroundImage = (
          element as any
        ).dataset.originalBackground;
      } else {
        (element as HTMLElement).style.backgroundImage = '';
      }
    }

    // Clean up tracking attributes
    delete (element as any).dataset.campaignImageModified;
    delete (element as any).dataset.campaignImageUrl;
    delete (element as any).dataset.originalSrc;
    delete (element as any).dataset.originalAlt;
    delete (element as any).dataset.originalBackground;
  }
}
